import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Globe, 
  DollarSign, 
  Bell, 
  Shield, 
  Mail, 
  Phone, 
  Database, 
  Key,
  Save,
  RefreshCw,
  Upload,
  Download,
  Clock,
  MapPin,
  Users,
  Zap,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertTriangle,
  Info
} from 'lucide-react';
import AdminLayout from '../common/AdminLayout';

interface AdminSettings {
  platform: {
    name: string;
    description: string;
    logo: string;
    favicon: string;
    timezone: string;
    language: string;
    currency: string;
    dateFormat: string;
    maintenanceMode: boolean;
  };
  commission: {
    platformFee: number;
    serviceFee: number;
    paymentProcessingFee: number;
    cancellationFee: number;
    minimumBookingAmount: number;
  };
  booking: {
    advanceBookingDays: number;
    cancellationPolicyHours: number;
    rescheduleLimit: number;
    autoAssignment: boolean;
    requireDeposit: boolean;
    depositPercentage: number;
  };
  notifications: {
    emailEnabled: boolean;
    smsEnabled: boolean;
    pushEnabled: boolean;
    adminAlerts: boolean;
    userWelcomeEmail: boolean;
    bookingConfirmations: boolean;
    paymentNotifications: boolean;
  };
  security: {
    twoFactorEnabled: boolean;
    sessionTimeout: number;
    maxLoginAttempts: number;
    passwordExpiry: number;
    ipWhitelist: string[];
    allowedDomains: string[];
  };
  integrations: {
    googleMapsApiKey: string;
    emailProvider: string;
    emailApiKey: string;
    smsProvider: string;
    smsApiKey: string;
    paymentGateway: string;
    paymentApiKey: string;
    analyticsId: string;
  };
}

const AdminSettings: React.FC = () => {
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('platform');
  const [showApiKeys, setShowApiKeys] = useState<{[key: string]: boolean}>({});
  const [changes, setChanges] = useState<{[key: string]: any}>({});

  // Mock data for demonstration
  useEffect(() => {
    setTimeout(() => {
      setSettings({
        platform: {
          name: 'SkillSeek',
          description: 'Professional skilled worker marketplace platform',
          logo: '/assets/logo.png',
          favicon: '/assets/favicon.ico',
          timezone: 'America/Jamaica',
          language: 'en',
          currency: 'USD',
          dateFormat: 'MM/DD/YYYY',
          maintenanceMode: false
        },
        commission: {
          platformFee: 10,
          serviceFee: 5,
          paymentProcessingFee: 2.9,
          cancellationFee: 15,
          minimumBookingAmount: 25
        },
        booking: {
          advanceBookingDays: 30,
          cancellationPolicyHours: 24,
          rescheduleLimit: 2,
          autoAssignment: true,
          requireDeposit: false,
          depositPercentage: 20
        },
        notifications: {
          emailEnabled: true,
          smsEnabled: true,
          pushEnabled: true,
          adminAlerts: true,
          userWelcomeEmail: true,
          bookingConfirmations: true,
          paymentNotifications: true
        },
        security: {
          twoFactorEnabled: true,
          sessionTimeout: 30,
          maxLoginAttempts: 5,
          passwordExpiry: 90,
          ipWhitelist: ['192.168.1.0/24', '10.0.0.0/8'],
          allowedDomains: ['skillseek.com', 'admin.skillseek.com']
        },
        integrations: {
          googleMapsApiKey: 'AIzaSyBxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
          emailProvider: 'SendGrid',
          emailApiKey: 'SG.xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
          smsProvider: 'Twilio',
          smsApiKey: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
          paymentGateway: 'Stripe',
          paymentApiKey: 'sk_test_xxxxxxxxxxxxxxxxxxxxxxxxxx',
          analyticsId: 'GA-XXXXXXXXX-X'
        }
      });
      setLoading(false);
    }, 1000);
  }, []);

  const handleInputChange = (section: string, field: string, value: any) => {
    if (!settings) return;
    
    const newSettings = {
      ...settings,
      [section]: {
        ...settings[section as keyof AdminSettings],
        [field]: value
      }
    };
    
    setSettings(newSettings);
    setChanges({ ...changes, [`${section}.${field}`]: value });
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    // Simulate API call
    setTimeout(() => {
      setSaving(false);
      setChanges({});
      // Show success message
    }, 2000);
  };

  const toggleApiKeyVisibility = (key: string) => {
    setShowApiKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const maskApiKey = (key: string) => {
    if (!key) return '';
    const visibleChars = 8;
    if (key.length <= visibleChars) return key;
    return key.substring(0, visibleChars) + 'x'.repeat(key.length - visibleChars);
  };

  const tabs = [
    { id: 'platform', label: 'Platform Settings', icon: Globe },
    { id: 'commission', label: 'Commission & Fees', icon: DollarSign },
    { id: 'booking', label: 'Booking Rules', icon: Clock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'integrations', label: 'Integrations', icon: Zap }
  ];

  if (loading) {
    return (
      <AdminLayout title="Settings & Configuration" subtitle="Configure platform settings and preferences">
        <div className="loading-spinner" />
      </AdminLayout>
    );
  }

  if (!settings) return null;

  return (
    <AdminLayout title="Settings & Configuration" subtitle="Configure platform settings and preferences">
      <div className="fade-in">
        {/* Settings Navigation */}
        <div className="admin-card" style={{ marginBottom: '25px' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={activeTab === tab.id ? 'btn-primary' : 'btn-secondary'}
                onClick={() => setActiveTab(tab.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Save Banner */}
        {Object.keys(changes).length > 0 && (
          <div className="admin-card" style={{ 
            marginBottom: '25px', 
            background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))',
            border: 'none'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <AlertTriangle size={20} />
                <div>
                  <div style={{ fontWeight: '600' }}>Unsaved Changes</div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>
                    You have {Object.keys(changes).length} unsaved changes
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  className="btn-secondary"
                  onClick={() => {
                    setChanges({});
                    window.location.reload();
                  }}
                >
                  Discard
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleSaveSettings}
                  disabled={saving}
                  style={{ background: 'white', color: 'var(--accent-blue)' }}
                >
                  {saving ? (
                    <>
                      <RefreshCw size={18} className="spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Platform Settings */}
        {activeTab === 'platform' && (
          <div className="admin-card">
            <div className="card-header">
              <div className="card-title">Platform Configuration</div>
              <div className="card-subtitle">Basic platform settings and branding</div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
              <div>
                <div className="form-group">
                  <label className="form-label">Platform Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settings.platform.name}
                    onChange={(e) => handleInputChange('platform', 'name', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={settings.platform.description}
                    onChange={(e) => handleInputChange('platform', 'description', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Timezone</label>
                  <select
                    className="form-control"
                    value={settings.platform.timezone}
                    onChange={(e) => handleInputChange('platform', 'timezone', e.target.value)}
                  >
                    <option value="America/Jamaica">America/Jamaica (UTC-5)</option>
                    <option value="America/New_York">America/New_York (UTC-5)</option>
                    <option value="Europe/London">Europe/London (UTC+0)</option>
                    <option value="Asia/Tokyo">Asia/Tokyo (UTC+9)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Default Language</label>
                  <select
                    className="form-control"
                    value={settings.platform.language}
                    onChange={(e) => handleInputChange('platform', 'language', e.target.value)}
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                  </select>
                </div>
              </div>

              <div>
                <div className="form-group">
                  <label className="form-label">Currency</label>
                  <select
                    className="form-control"
                    value={settings.platform.currency}
                    onChange={(e) => handleInputChange('platform', 'currency', e.target.value)}
                  >
                    <option value="USD">USD ($)</option>
                    <option value="JMD">JMD (J$)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Date Format</label>
                  <select
                    className="form-control"
                    value={settings.platform.dateFormat}
                    onChange={(e) => handleInputChange('platform', 'dateFormat', e.target.value)}
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Platform Logo</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="text"
                      className="form-control"
                      value={settings.platform.logo}
                      onChange={(e) => handleInputChange('platform', 'logo', e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button className="btn-secondary">
                      <Upload size={18} />
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={settings.platform.maintenanceMode}
                      onChange={(e) => handleInputChange('platform', 'maintenanceMode', e.target.checked)}
                    />
                    <span className="form-label" style={{ margin: 0 }}>Maintenance Mode</span>
                  </label>
                  <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)', marginTop: '5px' }}>
                    Enable to display maintenance page to users
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Commission Settings */}
        {activeTab === 'commission' && (
          <div className="admin-card">
            <div className="card-header">
              <div className="card-title">Commission & Fee Structure</div>
              <div className="card-subtitle">Configure platform fees and commissions</div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
              <div>
                <div className="form-group">
                  <label className="form-label">Platform Fee (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.commission.platformFee}
                    onChange={(e) => handleInputChange('commission', 'platformFee', parseFloat(e.target.value))}
                    min="0"
                    max="50"
                    step="0.1"
                  />
                  <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)', marginTop: '5px' }}>
                    Percentage taken from each completed booking
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Service Fee (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.commission.serviceFee}
                    onChange={(e) => handleInputChange('commission', 'serviceFee', parseFloat(e.target.value))}
                    min="0"
                    max="20"
                    step="0.1"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Payment Processing Fee (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.commission.paymentProcessingFee}
                    onChange={(e) => handleInputChange('commission', 'paymentProcessingFee', parseFloat(e.target.value))}
                    min="0"
                    max="10"
                    step="0.1"
                  />
                </div>
              </div>

              <div>
                <div className="form-group">
                  <label className="form-label">Cancellation Fee ($)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.commission.cancellationFee}
                    onChange={(e) => handleInputChange('commission', 'cancellationFee', parseFloat(e.target.value))}
                    min="0"
                    step="1"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Minimum Booking Amount ($)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.commission.minimumBookingAmount}
                    onChange={(e) => handleInputChange('commission', 'minimumBookingAmount', parseFloat(e.target.value))}
                    min="0"
                    step="1"
                  />
                </div>

                <div className="admin-card" style={{ 
                  background: 'var(--tertiary-black)', 
                  padding: '15px',
                  border: '1px solid var(--border-dark)'
                }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}>
                    Fee Calculator Preview
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                    For a $100 booking:
                    <div style={{ marginTop: '5px' }}>
                      • Platform Fee: ${(100 * settings.commission.platformFee / 100).toFixed(2)}
                    </div>
                    <div>
                      • Service Fee: ${(100 * settings.commission.serviceFee / 100).toFixed(2)}
                    </div>
                    <div>
                      • Processing Fee: ${(100 * settings.commission.paymentProcessingFee / 100).toFixed(2)}
                    </div>
                    <div style={{ fontWeight: '600', marginTop: '5px' }}>
                      • Worker Receives: ${(100 - (100 * (settings.commission.platformFee + settings.commission.serviceFee + settings.commission.paymentProcessingFee) / 100)).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Booking Settings */}
        {activeTab === 'booking' && (
          <div className="admin-card">
            <div className="card-header">
              <div className="card-title">Booking Rules & Policies</div>
              <div className="card-subtitle">Configure booking and cancellation policies</div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
              <div>
                <div className="form-group">
                  <label className="form-label">Advance Booking Limit (Days)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.booking.advanceBookingDays}
                    onChange={(e) => handleInputChange('booking', 'advanceBookingDays', parseInt(e.target.value))}
                    min="1"
                    max="365"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Cancellation Policy (Hours)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.booking.cancellationPolicyHours}
                    onChange={(e) => handleInputChange('booking', 'cancellationPolicyHours', parseInt(e.target.value))}
                    min="1"
                    max="168"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Reschedule Limit</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.booking.rescheduleLimit}
                    onChange={(e) => handleInputChange('booking', 'rescheduleLimit', parseInt(e.target.value))}
                    min="0"
                    max="10"
                  />
                </div>
              </div>

              <div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={settings.booking.autoAssignment}
                      onChange={(e) => handleInputChange('booking', 'autoAssignment', e.target.checked)}
                    />
                    <span className="form-label" style={{ margin: 0 }}>Auto-Assignment</span>
                  </label>
                  <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)', marginTop: '5px' }}>
                    Automatically assign nearest available workers
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={settings.booking.requireDeposit}
                      onChange={(e) => handleInputChange('booking', 'requireDeposit', e.target.checked)}
                    />
                    <span className="form-label" style={{ margin: 0 }}>Require Deposit</span>
                  </label>
                </div>

                {settings.booking.requireDeposit && (
                  <div className="form-group">
                    <label className="form-label">Deposit Percentage (%)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={settings.booking.depositPercentage}
                      onChange={(e) => handleInputChange('booking', 'depositPercentage', parseInt(e.target.value))}
                      min="10"
                      max="100"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notification Settings */}
        {activeTab === 'notifications' && (
          <div className="admin-card">
            <div className="card-header">
              <div className="card-title">Notification Preferences</div>
              <div className="card-subtitle">Configure notification channels and preferences</div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px' }}>
                    Communication Channels
                  </div>
                  
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={settings.notifications.emailEnabled}
                        onChange={(e) => handleInputChange('notifications', 'emailEnabled', e.target.checked)}
                      />
                      <Mail size={16} />
                      <span className="form-label" style={{ margin: 0 }}>Email Notifications</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={settings.notifications.smsEnabled}
                        onChange={(e) => handleInputChange('notifications', 'smsEnabled', e.target.checked)}
                      />
                      <Phone size={16} />
                      <span className="form-label" style={{ margin: 0 }}>SMS Notifications</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={settings.notifications.pushEnabled}
                        onChange={(e) => handleInputChange('notifications', 'pushEnabled', e.target.checked)}
                      />
                      <Bell size={16} />
                      <span className="form-label" style={{ margin: 0 }}>Push Notifications</span>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px' }}>
                    Notification Types
                  </div>
                  
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={settings.notifications.adminAlerts}
                        onChange={(e) => handleInputChange('notifications', 'adminAlerts', e.target.checked)}
                      />
                      <span className="form-label" style={{ margin: 0 }}>Admin Alerts</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={settings.notifications.userWelcomeEmail}
                        onChange={(e) => handleInputChange('notifications', 'userWelcomeEmail', e.target.checked)}
                      />
                      <span className="form-label" style={{ margin: 0 }}>Welcome Emails</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={settings.notifications.bookingConfirmations}
                        onChange={(e) => handleInputChange('notifications', 'bookingConfirmations', e.target.checked)}
                      />
                      <span className="form-label" style={{ margin: 0 }}>Booking Confirmations</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={settings.notifications.paymentNotifications}
                        onChange={(e) => handleInputChange('notifications', 'paymentNotifications', e.target.checked)}
                      />
                      <span className="form-label" style={{ margin: 0 }}>Payment Notifications</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Security Settings */}
        {activeTab === 'security' && (
          <div className="admin-card">
            <div className="card-header">
              <div className="card-title">Security Configuration</div>
              <div className="card-subtitle">Configure security policies and access controls</div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
              <div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={settings.security.twoFactorEnabled}
                      onChange={(e) => handleInputChange('security', 'twoFactorEnabled', e.target.checked)}
                    />
                    <span className="form-label" style={{ margin: 0 }}>Two-Factor Authentication</span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="form-label">Session Timeout (Minutes)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.security.sessionTimeout}
                    onChange={(e) => handleInputChange('security', 'sessionTimeout', parseInt(e.target.value))}
                    min="5"
                    max="480"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Max Login Attempts</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.security.maxLoginAttempts}
                    onChange={(e) => handleInputChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                    min="3"
                    max="10"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Password Expiry (Days)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.security.passwordExpiry}
                    onChange={(e) => handleInputChange('security', 'passwordExpiry', parseInt(e.target.value))}
                    min="30"
                    max="365"
                  />
                </div>
              </div>

              <div>
                <div className="form-group">
                  <label className="form-label">IP Whitelist</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={settings.security.ipWhitelist.join('\n')}
                    onChange={(e) => handleInputChange('security', 'ipWhitelist', e.target.value.split('\n'))}
                    placeholder="Enter IP addresses or ranges, one per line"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Allowed Domains</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={settings.security.allowedDomains.join('\n')}
                    onChange={(e) => handleInputChange('security', 'allowedDomains', e.target.value.split('\n'))}
                    placeholder="Enter allowed domains, one per line"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Integrations */}
        {activeTab === 'integrations' && (
          <div className="admin-card">
            <div className="card-header">
              <div className="card-title">Third-Party Integrations</div>
              <div className="card-subtitle">Configure external service API keys and settings</div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
              <div>
                <div className="form-group">
                  <label className="form-label">Google Maps API Key</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type={showApiKeys.googleMaps ? 'text' : 'password'}
                      className="form-control"
                      value={settings.integrations.googleMapsApiKey}
                      onChange={(e) => handleInputChange('integrations', 'googleMapsApiKey', e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button 
                      className="btn-secondary"
                      onClick={() => toggleApiKeyVisibility('googleMaps')}
                      style={{ padding: '10px', minWidth: 'auto' }}
                    >
                      {showApiKeys.googleMaps ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Email Provider</label>
                  <select
                    className="form-control"
                    value={settings.integrations.emailProvider}
                    onChange={(e) => handleInputChange('integrations', 'emailProvider', e.target.value)}
                  >
                    <option value="SendGrid">SendGrid</option>
                    <option value="Mailgun">Mailgun</option>
                    <option value="Amazon SES">Amazon SES</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Email API Key</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type={showApiKeys.email ? 'text' : 'password'}
                      className="form-control"
                      value={settings.integrations.emailApiKey}
                      onChange={(e) => handleInputChange('integrations', 'emailApiKey', e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button 
                      className="btn-secondary"
                      onClick={() => toggleApiKeyVisibility('email')}
                      style={{ padding: '10px', minWidth: 'auto' }}
                    >
                      {showApiKeys.email ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">SMS Provider</label>
                  <select
                    className="form-control"
                    value={settings.integrations.smsProvider}
                    onChange={(e) => handleInputChange('integrations', 'smsProvider', e.target.value)}
                  >
                    <option value="Twilio">Twilio</option>
                    <option value="Nexmo">Nexmo</option>
                    <option value="Amazon SNS">Amazon SNS</option>
                  </select>
                </div>
              </div>

              <div>
                <div className="form-group">
                  <label className="form-label">SMS API Key</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type={showApiKeys.sms ? 'text' : 'password'}
                      className="form-control"
                      value={settings.integrations.smsApiKey}
                      onChange={(e) => handleInputChange('integrations', 'smsApiKey', e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button 
                      className="btn-secondary"
                      onClick={() => toggleApiKeyVisibility('sms')}
                      style={{ padding: '10px', minWidth: 'auto' }}
                    >
                      {showApiKeys.sms ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Payment Gateway</label>
                  <select
                    className="form-control"
                    value={settings.integrations.paymentGateway}
                    onChange={(e) => handleInputChange('integrations', 'paymentGateway', e.target.value)}
                  >
                    <option value="Stripe">Stripe</option>
                    <option value="PayPal">PayPal</option>
                    <option value="Square">Square</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Payment API Key</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type={showApiKeys.payment ? 'text' : 'password'}
                      className="form-control"
                      value={settings.integrations.paymentApiKey}
                      onChange={(e) => handleInputChange('integrations', 'paymentApiKey', e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button 
                      className="btn-secondary"
                      onClick={() => toggleApiKeyVisibility('payment')}
                      style={{ padding: '10px', minWidth: 'auto' }}
                    >
                      {showApiKeys.payment ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Analytics ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settings.integrations.analyticsId}
                    onChange={(e) => handleInputChange('integrations', 'analyticsId', e.target.value)}
                    placeholder="GA-XXXXXXXXX-X"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings; 